import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
    ProductivityClient,
    INextViewScoreQuery,
    NextViewScoreQuery,
    ProductivityReportType,
    NextViewScoreExportQuery,
    ShiftType,
    ScoreDto,
} from '@services';
import {
    DxButtonComponent,
    DxDateRangeBoxComponent,
    DxSelectBoxComponent,
} from 'devextreme-angular';
import {
    ChartDataPoint,
    DoughnutChartComponent,
} from 'src/app/components/library/doughnut-chart/doughnut-chart.component';
import { CapitalCasePipe } from 'src/app/pipes/capital-case.pipe';
import { FileUtils } from 'src/app/shared/file-utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';
import EnumUtils from 'src/app/shared/enum-utils';
import { NextViewDoughnutChartComponent } from './next-view-doughnut-chart/next-view-doughnut-chart.component';

export function nextViewPalette(): Record<string, string> {
    const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
    return {
        [ProductivityReportType.CasePicking]: getColor('--bs-success'),
        [ProductivityReportType.LayerPicking]: getColor('--bs-danger'),
        [ProductivityReportType.PalletPicking]: getColor('--bs-warning'),
        [ProductivityReportType.Putaway]: getColor('--bs-info'),
        [ProductivityReportType.Refill]: getColor('--bs-gray-500'),
    };
}

@Component({
    selector: 'app-next-view-score',
    standalone: true,
    imports: [
        CommonModule,
        DoughnutChartComponent,
        DxDateRangeBoxComponent,
        DxButtonComponent,
        DxSelectBoxComponent,
        CapitalCasePipe,
        NextViewDoughnutChartComponent,        
        DecimalPipe,
    ],
    templateUrl: './next-view-score.component.html',
})
export class NextViewScoreComponent implements OnInit {
    private productivityClient = inject(ProductivityClient);
    private destroyRef = inject(DestroyRef);

    readonly ProductivityReportType = ProductivityReportType;
    readonly productivityTypes: ProductivityReportType[] = [
        ProductivityReportType.CasePicking,
        ProductivityReportType.LayerPicking,
        ProductivityReportType.PalletPicking,
        ProductivityReportType.Putaway,
        ProductivityReportType.Refill,
    ];

    shiftOptions: Array<{ value: ShiftType | undefined, text: string }> = [
        { value: undefined, text: 'All' },
        ...EnumUtils.getEnumOptions(ShiftType),
    ];

    shift: ShiftType | undefined = undefined;
    scoreData: ChartDataPoint[] = [];
    daysLookBack: number = 0;

    dateRangeFilterValue: [Date, Date] = [
        new Date(new Date().setHours(0, 0, 0, 0)),
        new Date(new Date().setHours(0, 0, 0, 0)),
    ];

    nextViewItemByType: { [key in ProductivityReportType]: ChartDataPoint | null } = {
        [ProductivityReportType.CasePicking]: null,
        [ProductivityReportType.LayerPicking]: null,
        [ProductivityReportType.PalletPicking]: null,
        [ProductivityReportType.Putaway]: null,
        [ProductivityReportType.Refill]: null,
    };

    palette: Record<string, string> = {};

    ngOnInit(): void {
        this.initDateRange();
        this.palette = nextViewPalette();
    }

    loadScoreData = (query: INextViewScoreQuery) => {
        return this.productivityClient.getNextViewScore(new NextViewScoreQuery({ ...query, shiftType: this.shift }))
            .pipe(tap((score) => this.storeNextViewData(score)));
    };

    get fromDate(): Date {
        return this.dateRangeFilterValue?.[0];
    }

    get toDate(): Date {
        return this.dateRangeFilterValue?.[1];
    }

    onExport() {
        const query = new NextViewScoreExportQuery({
            shiftType: this.shift,
            from: this.fromDate,
            to: this.toDate,
        });
        this.productivityClient
            .exportNextViewScore(query)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => FileUtils.downloadFile(res));
    }

    onMainChartLoaded(items: ChartDataPoint[]) {
        this.scoreData = items;
    }

    showChart(productivityType: ProductivityReportType): boolean {
        return !!this.nextViewItemByType[productivityType];
    }

    private initDateRange(): void {
        if (this.daysLookBack > 0) {
            this.dateRangeFilterValue = [
                new Date(Date.now() - this.daysLookBack * 24 * 60 * 60 * 1000),
                new Date(),
            ];
        }
    }

    private storeNextViewData(score: ScoreDto): void {
        const totals = Object.entries(score.totals ?? {}).map(([argument, value]) => ({
            argument,
            value,
        }));

        this.productivityTypes.forEach((type) => {
            this.nextViewItemByType[type] =
                totals.find((item) => item.argument === type) ?? null;
        });
    }
}
